"use client";

/**
 * Swipe-down-to-close gesture for mobile bottom sheets.
 *
 * Extracted from MobileMoreSheet's drag-handle gesture so every sheet
 * shares one behaviour: a touch-drag down on the sheet's top zone
 * (handle pill / header row) tracks the finger 1:1, releasing past the
 * threshold closes the sheet, releasing earlier springs it back.
 *
 * Usage:
 *   const { offset, dragging, handleProps } = useSheetDrag(onClose, open);
 *   <div style={{
 *          transform: `translateY(${offset}px)`,
 *          transition: dragging ? "none" : "<the sheet's own transition>",
 *        }}>
 *     <div {...handleProps} className="... touch-none">header / pill</div>
 *
 * Notes:
 *   - Touch-only and inert at >= sm widths: most of these surfaces are
 *     the SAME DOM as a desktop drawer / centered modal, which must
 *     not drag.
 *   - Put `touch-none` on the drag zone so the browser doesn't fight
 *     the gesture with scrolling / pull-to-refresh. The zone must be a
 *     non-scrolling area (handle or header), never the sheet body.
 *   - On a past-threshold release the offset is intentionally NOT
 *     reset: sheets with an exit transition continue downward from
 *     where the finger let go instead of snapping back to 0 first.
 *     The `open` flag re-arms a clean state for the next presentation
 *     (matters for sheets that stay mounted while closed).
 *   - Sheets whose enter animation holds the `transform` property
 *     (CSS keyframes with fill) should apply the offset via the
 *     standalone `translate` property instead - they compose.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const CLOSE_THRESHOLD_PX = 80;

export interface SheetDragHandleProps {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
}

const FLING_PX_PER_MS_DRAG = 0.55;
const NO_DRAG_SELECTOR = "input, textarea, select, [role='slider'], [data-sheet-no-drag], .mapboxgl-map";

/** Nearest scrollable ancestor of `node` inside `root`, or null. */
function scrollerBetween(node: HTMLElement | null, root: HTMLElement): HTMLElement | null {
  let cur: HTMLElement | null = node;
  while (cur && cur !== root) {
    const oy = getComputedStyle(cur).overflowY;
    if ((oy === "auto" || oy === "scroll") && cur.scrollHeight > cur.clientHeight + 1) return cur;
    cur = cur.parentElement;
  }
  return null;
}

export function useSheetDrag(onClose: () => void, open: boolean = true) {
  const startY = useRef<number | null>(null);
  // State drives the render; the ref mirrors it so touchend reads the
  // final value without depending on closure freshness.
  const offsetRef = useRef(0);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const update = (v: number) => {
    offsetRef.current = v;
    setOffset(v);
  };

  // Re-arm on every open so a stay-mounted sheet doesn't reopen still
  // translated by the previous drag's release offset.
  useEffect(() => {
    if (!open) return;
    startY.current = null;
    offsetRef.current = 0;
    setOffset(0);
    setDragging(false);
  }, [open]);

  const end = () => {
    if (startY.current == null) return;
    startY.current = null;
    setDragging(false);
    if (offsetRef.current > CLOSE_THRESHOLD_PX) {
      onCloseRef.current();
    } else {
      update(0);
    }
  };

  // ── Whole-panel drag (owner, 2026-09-20: "closable if you drag not
  //    from the top but from the centre"). Attach `panelRef` to the
  //    sheet's translating element. Native listeners (React's touchmove
  //    is passive, and iOS needs preventDefault to stop the body from
  //    scrolling or bouncing under the finger). A drag that starts
  //    inside a scrolled list keeps scrolling; one that starts at the
  //    top of a list, or outside any list, drags the sheet. When the
  //    panel is bound, the handle's React handlers step aside so a
  //    touch on the handle is not counted twice.
  const [panelEl, setPanelEl] = useState<HTMLElement | null>(null);
  const panelRef = useCallback((el: HTMLElement | null) => setPanelEl(el), []);
  const panelBound = panelEl !== null;
  useEffect(() => {
    const el = panelEl;
    if (!el || !open) return;
    let sx: number | null = null;
    let sy: number | null = null;
    let active = false;
    let released = false;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    const onStart = (e: TouchEvent) => {
      if (window.matchMedia("(min-width: 640px)").matches) return;
      const t = e.touches[0];
      sx = t.clientX; sy = t.clientY; lastY = t.clientY; lastT = e.timeStamp;
      velocity = 0; active = false; released = false;
    };
    const onMove = (e: TouchEvent) => {
      if (sx == null || sy == null || released) return;
      const t = e.touches[0];
      const dx = t.clientX - sx;
      const dy = t.clientY - sy;
      if (!active) {
        const target = e.target as HTMLElement | null;
        if (target?.closest(NO_DRAG_SELECTOR)) { released = true; return; }
        const scroller = scrollerBetween(target, el);
        const atTop = !scroller || scroller.scrollTop <= 0;
        // Inside a horizontal swiper (photo carousel) the gesture must be
        // clearly vertical, or a slightly diagonal photo swipe would also
        // start dragging the sheet.
        const ratio = target?.closest("[data-swipe-x]") ? 2 : 1;
        if (dy > 0 && dy >= Math.abs(dx) * ratio && atTop) {
          active = true;
          startY.current = null; // the handle handlers stand down
          setDragging(true);
        } else {
          released = true;
          return;
        }
      }
      if (e.cancelable) e.preventDefault();
      const dt = e.timeStamp - lastT;
      if (dt > 0) velocity = (t.clientY - lastY) / dt;
      lastY = t.clientY; lastT = e.timeStamp;
      update(Math.max(0, dy));
    };
    const onEnd = () => {
      if (sy == null) return;
      sx = null; sy = null;
      if (!active) return;
      active = false;
      setDragging(false);
      const fling = velocity > FLING_PX_PER_MS_DRAG && offsetRef.current > 24;
      if (offsetRef.current > CLOSE_THRESHOLD_PX || fling) onCloseRef.current();
      else update(0);
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelEl, open]);

  const handleProps: SheetDragHandleProps = {
    onTouchStart: (e) => {
      // With the whole panel bound, the native listeners own the gesture.
      if (panelBound) return;
      // Desktop drawers / modals share this DOM - only arm below sm.
      if (window.matchMedia("(min-width: 640px)").matches) return;
      startY.current = e.touches[0].clientY;
      update(0);
      setDragging(true);
    },
    onTouchMove: (e) => {
      if (startY.current == null) return;
      // Downward only - the sheet doesn't expand further up.
      update(Math.max(0, e.touches[0].clientY - startY.current));
    },
    onTouchEnd: end,
    onTouchCancel: end,
  };

  return { offset, dragging, handleProps, panelRef };
}

/* ── Whole-sheet swipe (full-screen mobile sheets) ─────────────────── */

const FLING_PX_PER_MS = 0.55;

/**
 * Instagram-style dismissal for a FULL-SCREEN mobile sheet: a downward
 * drag that starts anywhere on the sheet follows the finger as long as
 * the scroll body is at its top, and releasing past the threshold OR
 * with a downward fling closes it. A drag that begins mostly sideways
 * is left to the photo carousel; one that begins with the body
 * scrolled just scrolls.
 *
 * Native listeners on the panel element, not React props: React
 * registers touchmove as passive, so preventDefault (needed to stop
 * iOS from rubber-banding the body under the finger) is a no-op there.
 *
 * Inert at >= sm (the same DOM is the desktop drawer).
 */
export function useSheetSwipe(
  panelRef: React.RefObject<HTMLElement | null>,
  scrollRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  open: boolean = true,
) {
  const offsetRef = useRef(0);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    offsetRef.current = 0;
    setOffset(0);
    setDragging(false);
    const el = panelRef.current;
    if (!el) return;

    let startX: number | null = null;
    let startY: number | null = null;
    let active = false;
    let released = false;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;

    const set = (v: number) => {
      offsetRef.current = v;
      setOffset(v);
    };

    const onStart = (e: TouchEvent) => {
      if (window.matchMedia("(min-width: 640px)").matches) return;
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      lastY = t.clientY;
      lastT = e.timeStamp;
      velocity = 0;
      active = false;
      released = false;
    };

    const onMove = (e: TouchEvent) => {
      if (startY == null || startX == null || released) return;
      const t = e.touches[0];
      const dy = t.clientY - startY;
      const dx = t.clientX - startX;
      if (!active) {
        // Decide on the FIRST move: iOS commits to its own scroll on
        // that event, so a later preventDefault would be ignored.
        const target = e.target as HTMLElement | null;
        const nativeZone = target?.closest(".mapboxgl-map, textarea, input, [data-sheet-no-drag]");
        const scroller = scrollRef.current;
        const atTop = !scroller || scroller.scrollTop <= 0;
        const ratio = target?.closest("[data-swipe-x]") ? 2 : 1;
        if (dy > 0 && dy >= Math.abs(dx) * ratio && atTop && !nativeZone) {
          active = true;
          setDragging(true);
        } else {
          released = true;
          return;
        }
      }
      if (e.cancelable) e.preventDefault();
      const dt = e.timeStamp - lastT;
      if (dt > 0) velocity = (t.clientY - lastY) / dt;
      lastY = t.clientY;
      lastT = e.timeStamp;
      set(Math.max(0, dy));
    };

    const onEnd = () => {
      if (startY == null) return;
      startY = null;
      startX = null;
      if (!active) return;
      active = false;
      setDragging(false);
      const fling = velocity > FLING_PX_PER_MS && offsetRef.current > 24;
      if (offsetRef.current > CLOSE_THRESHOLD_PX || fling) {
        onCloseRef.current();
      } else {
        set(0);
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [open, panelRef, scrollRef]);

  return { offset, dragging };
}
